/**
 * Visual Content Editing — Content Registry.
 *
 * Mappa i `data-cid` REALI di home/hero → metadata editabile dall'overlay
 * Visual Editor. Ogni voce descrive:
 *  - quale elemento DOM è editabile (`cid` === valore di `data-cid`)
 *  - che tipo di contenuto è (`type`: text | image | link)
 *  - dove vive il valore dentro `settings.page_content` (`path`, dot-notation)
 *  - se è multilingua (`i18n`) → il valore vive sotto `page_content[locale][path]`
 *
 * I `data-cid` qui elencati DEVONO esistere nei componenti reali. I componenti
 * target leggono il valore via `useContent(path, fallback)` con fallback ===
 * testo hardcoded attuale, così il DB è puramente additivo (zero regressioni).
 *
 * Fase 1 (MVP): solo home premium hero + sezione ricerca. Fasi 2/3 estenderanno
 * a catalogo/boat.
 */

export type ContentFieldType = 'text' | 'image' | 'link';

export interface ContentField {
  /** Valore del `data-cid` dell'elemento DOM editabile. */
  cid: string;
  /** Tipo di contenuto editabile. */
  type: ContentFieldType;
  /** Etichetta leggibile mostrata nel pannello editor (admin-facing, IT). */
  label: string;
  /** Path JSON dentro `page_content` (dot-notation), es. 'hero.title'. */
  path: string;
  /**
   * true → testo multilingua: il valore vive in `page_content[locale][path]`.
   * false/undefined → valore unico (es. immagini) in `page_content.shared[path]`.
   */
  i18n?: boolean;
  /** Hint UI opzionale per il pannello (placeholder / aiuto). */
  hint?: string;
  /**
   * Raggruppamento per pagina (usato SOLO dal form "codice per pagina" del modulo
   * embed booking, /admin/embed-designer). undefined = non raggruppato (es. home).
   */
  page?: 'embed-catalog' | 'embed-step1' | 'embed-step2' | 'embed-confirmed';
}

/** Etichette leggibili dei gruppi `page` (per il picker admin). */
export const CONTENT_PAGE_LABELS: Record<string, string> = {
  'embed-catalog': 'Catalogo',
  'embed-step1': 'Step 1 · Disponibilità',
  'embed-step2': 'Step 2 · Dati e pagamento',
  'embed-confirmed': 'Conferma',
};

/**
 * Regioni editabili della HOME PREMIUM (hero overlay #top) + sezione ricerca.
 * Sorgente componente: components/HomeCatalogClient.tsx (sezione premium-top-overlay)
 * + components/home/PremiumHero.tsx (background slideshow) + components/SearchSection.tsx.
 */
export const CONTENT_REGISTRY: ContentField[] = [
  {
    cid: 'premium-hero-badge',
    type: 'text',
    label: 'Hero · Badge (eyebrow)',
    path: 'hero.badge',
    i18n: true,
    hint: 'Testo della pill in alto, es. "Sardegna · Mediterraneo"',
  },
  {
    cid: 'premium-hero-title',
    type: 'text',
    label: 'Hero · Titolo',
    path: 'hero.title',
    i18n: true,
    hint: 'Headline principale. Gli a-capo (newline) sono preservati.',
  },
  {
    cid: 'premium-hero-subtitle',
    type: 'text',
    label: 'Hero · Sottotitolo',
    path: 'hero.subtitle',
    i18n: true,
    hint: 'Paragrafo descrittivo sotto il titolo.',
  },
  {
    cid: 'premium-hero-cta-rentals',
    type: 'text',
    label: 'Hero · CTA noleggi (testo)',
    path: 'hero.ctaRentals',
    i18n: true,
    hint: 'Etichetta del bottone primario.',
  },
  {
    cid: 'premium-hero-cta-experiences',
    type: 'text',
    label: 'Hero · CTA esperienze (testo)',
    path: 'hero.ctaExperiences',
    i18n: true,
    hint: 'Etichetta del bottone secondario.',
  },
  {
    cid: 'premium-hero-bg-image',
    type: 'image',
    label: 'Hero · Immagine di sfondo',
    path: 'hero.bgImage',
    i18n: false,
    hint: 'Override prima slide hero. Vuoto = slideshow da settings hero_background.',
  },
  {
    cid: 'home-search-section-title',
    type: 'text',
    label: 'Ricerca · Titolo sezione',
    path: 'searchSection.title',
    i18n: true,
    hint: 'Titolo sopra la barra di ricerca.',
  },
  {
    cid: 'home-search-section-subtitle',
    type: 'text',
    label: 'Ricerca · Sottotitolo sezione',
    path: 'searchSection.subtitle',
    i18n: true,
    hint: 'Sottotitolo sopra la barra di ricerca.',
  },
  /**
   * Widget embed (/embed/[key]) — copy statico/boilerplate CONDIVISO da TUTTI i
   * vendor (un override cambia il testo ovunque). NON i dati dinamici per-barca
   * (nome/prezzo/immagine, quelli restano dal catalogo reale) né le
   * personalizzazioni per-vendor (custom_css/intro_html, vedi /admin/embed-designer
   * — sistema separato che coesiste con questo). Sorgenti: EmbedWidget.tsx,
   * EmbedCalendar.tsx, confirmed/page.tsx.
   */
  {
    cid: 'embed-catalog-title',
    type: 'text',
    label: 'Titolo catalogo',
    path: 'embed.catalog.title',
    i18n: true,
    hint: 'Titolo sopra la griglia barche, es. "Prenota online".',
    page: 'embed-catalog',
  },
  {
    cid: 'embed-catalog-empty',
    type: 'text',
    label: 'Catalogo vuoto',
    path: 'embed.catalog.empty',
    i18n: true,
    hint: 'Messaggio quando il vendor non ha barche pubblicate.',
    page: 'embed-catalog',
  },
  {
    cid: 'embed-price-suffix',
    type: 'text',
    label: 'Suffisso prezzo',
    path: 'embed.catalog.priceSuffix',
    i18n: true,
    hint: 'Testo dopo il prezzo sulla card barca, es. "/giorno".',
    page: 'embed-catalog',
  },
  {
    cid: 'embed-footer',
    type: 'text',
    label: 'Footer',
    path: 'embed.footer',
    i18n: true,
    hint: 'Riga in fondo al widget e alla pagina di conferma, es. "Powered by InsulaCharter".',
    page: 'embed-catalog',
  },
  {
    cid: 'embed-back-catalog',
    type: 'text',
    label: 'Link "torna al catalogo" (dal pannello prenotazione)',
    path: 'embed.step1.backToCatalog',
    i18n: true,
    page: 'embed-step1',
  },
  {
    cid: 'embed-guests-label',
    type: 'text',
    label: 'Etichetta "Ospiti"',
    path: 'embed.step1.guestsLabel',
    i18n: true,
    page: 'embed-step1',
  },
  {
    cid: 'embed-calendar-hint',
    type: 'text',
    label: 'Calendario, hint iniziale',
    path: 'embed.calendar.hint',
    i18n: true,
    hint: 'Testo mostrato sotto il calendario prima che l\'utente selezioni una data.',
    page: 'embed-step1',
  },
  {
    cid: 'embed-to-step2',
    type: 'text',
    label: 'Bottone "Prenota →"',
    path: 'embed.step1.nextButton',
    i18n: true,
    page: 'embed-step1',
  },
  {
    cid: 'embed-back-step1',
    type: 'text',
    label: 'Link "Modifica date"',
    path: 'embed.step2.backToStep1',
    i18n: true,
    page: 'embed-step2',
  },
  {
    cid: 'embed-name-label',
    type: 'text',
    label: 'Etichetta "Nome e cognome"',
    path: 'embed.step2.nameLabel',
    i18n: true,
    page: 'embed-step2',
  },
  {
    cid: 'embed-email-label',
    type: 'text',
    label: 'Etichetta "Email"',
    path: 'embed.step2.emailLabel',
    i18n: true,
    page: 'embed-step2',
  },
  {
    cid: 'embed-phone-label',
    type: 'text',
    label: 'Etichetta "Telefono"',
    path: 'embed.step2.phoneLabel',
    i18n: true,
    page: 'embed-step2',
  },
  {
    cid: 'embed-submit',
    type: 'text',
    label: 'Bottone "Prenota e paga" (prefisso, il prezzo si aggiunge da solo)',
    path: 'embed.step2.submitButton',
    i18n: true,
    page: 'embed-step2',
  },
  {
    cid: 'embed-confirmed-checking-title',
    type: 'text',
    label: 'Titolo "in verifica"',
    path: 'embed.confirmed.checkingTitle',
    i18n: true,
    page: 'embed-confirmed',
  },
  {
    cid: 'embed-confirmed-checking-body',
    type: 'text',
    label: 'Testo "in verifica"',
    path: 'embed.confirmed.checkingBody',
    i18n: true,
    page: 'embed-confirmed',
  },
  {
    cid: 'embed-confirmed-paid-title',
    type: 'text',
    label: 'Titolo pagamento riuscito',
    path: 'embed.confirmed.paidTitle',
    i18n: true,
    page: 'embed-confirmed',
  },
  {
    cid: 'embed-confirmed-paid-body',
    type: 'text',
    label: 'Testo pagamento riuscito',
    path: 'embed.confirmed.paidBody',
    i18n: true,
    page: 'embed-confirmed',
  },
  {
    cid: 'embed-confirmed-notpaid-title',
    type: 'text',
    label: 'Titolo pagamento non riuscito',
    path: 'embed.confirmed.notPaidTitle',
    i18n: true,
    page: 'embed-confirmed',
  },
  {
    cid: 'embed-confirmed-notpaid-body',
    type: 'text',
    label: 'Testo pagamento non riuscito',
    path: 'embed.confirmed.notPaidBody',
    i18n: true,
    page: 'embed-confirmed',
  },
  {
    cid: 'embed-confirmed-back',
    type: 'text',
    label: 'Link "torna al catalogo"',
    path: 'embed.confirmed.backLink',
    i18n: true,
    page: 'embed-confirmed',
  },
];

/** Lookup veloce per `data-cid` → ContentField. */
const REGISTRY_BY_CID: Record<string, ContentField> = CONTENT_REGISTRY.reduce(
  (acc, f) => {
    acc[f.cid] = f;
    return acc;
  },
  {} as Record<string, ContentField>,
);

/** Ritorna il ContentField associato a un `data-cid`, o null. */
export function getRegistryField(cid: string | null | undefined): ContentField | null {
  if (!cid) return null;
  return REGISTRY_BY_CID[cid] ?? null;
}

/** Set di tutti i `data-cid` registrati (per highlight rapido nell'overlay). */
export const REGISTRY_CIDS: ReadonlySet<string> = new Set(
  CONTENT_REGISTRY.map((f) => f.cid),
);
